import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, Wand2, Download, RefreshCw, Sparkles, ArrowRight, X, Image as ImageIcon } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { photoSwap, API_URL } from '../utils/api';
import AuthModal from './AuthModal';
import SEO from './SEO';

const PhotoSwapPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [templateImage, setTemplateImage] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [referenceImage, setReferenceImage] = useState(null);
  const [referencePreview, setReferencePreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useProModel, setUseProModel] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  
  const templateInputRef = useRef(null);
  const referenceInputRef = useRef(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTemplateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplateImage(file);
      setTemplatePreview(URL.createObjectURL(file));
      setResult(null);
      setError('');
    }
  };

  const handleReferenceUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReferenceImage(file);
      setReferencePreview(URL.createObjectURL(file));
      setResult(null);
      setError('');
    }
  };

  const clearTemplate = () => {
    setTemplateImage(null);
    setTemplatePreview(null);
    setResult(null);
    if (templateInputRef.current) templateInputRef.current.value = '';
  };

  const clearReference = () => {
    setReferenceImage(null);
    setReferencePreview(null);
    setResult(null);
    if (referenceInputRef.current) referenceInputRef.current.value = '';
  };

  const handleSwap = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    if (!templateImage || !referenceImage) {
      setError('Please upload both a template image and a reference image');
      return;
    }

    if (!prompt.trim()) {
      setError('Please describe what you want to swap');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const data = await photoSwap({
        prompt: prompt.trim(),
        templateImage,
        referenceImage,
        useProModel,
      });

      // Handle the result URL
      let resultUrl = data.public_url;
      if (resultUrl.startsWith('/uploads/')) {
        resultUrl = `${API_URL}${resultUrl}`;
      }
      setResult(resultUrl);
    } catch (err) {
      console.error('Photo swap error:', err);
      setError(err.response?.data?.detail || 'Photo swap failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    
    try {
      const response = await fetch(result);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `photo-swap-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleUseInCard = () => {
    if (!result) return;
    // Navigate to editing page with the result as background
    navigate('/edit', { state: { backgroundImage: result } });
  };

  const suggestedPrompts = [
    "Replace the face with my pet's face",
    "Swap the person with my photo",
    "Put my dog's face on the character",
    "Replace the subject with my child's photo",
  ];

  return (
    <>
      <SEO
        title="AI Photo Swap - Face & Subject Swapping"
        description="Swap faces and subjects in your photos with AI. Upload a template and reference image, describe what you want, and watch the magic happen."
        path="/photo-swap"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              AI-Powered
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-violet-700 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent mb-4">
              Photo Swap
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Seamlessly swap faces and subjects between images using advanced AI. 
              Perfect for creating personalized greeting cards!
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Upload Section */}
            <div className="space-y-6">
              {/* Template Image Upload */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <span className="bg-violet-100 text-violet-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                    Template Image
                  </h2>
                  {templatePreview && (
                    <button onClick={clearTemplate} className="text-gray-400 hover:text-red-500 transition">
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  The base image where you want to make the swap
                </p>
                
                {templatePreview ? (
                  <div className="relative rounded-xl overflow-hidden bg-gray-100">
                    <img src={templatePreview} alt="Template" className="w-full h-48 object-contain" />
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-purple-200 rounded-xl p-8 text-center hover:border-purple-400 hover:bg-purple-50 transition-all">
                      <ImageIcon className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                      <p className="text-purple-600 font-medium">Click to upload template</p>
                      <p className="text-sm text-gray-400 mt-1">PNG, JPG up to 10MB</p>
                    </div>
                    <input
                      ref={templateInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleTemplateUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Reference Image Upload */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-fuchsia-100">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <span className="bg-fuchsia-100 text-fuchsia-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                    Reference Image
                  </h2>
                  {referencePreview && (
                    <button onClick={clearReference} className="text-gray-400 hover:text-red-500 transition">
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  The image with the face/subject you want to use
                </p>
                
                {referencePreview ? (
                  <div className="relative rounded-xl overflow-hidden bg-gray-100">
                    <img src={referencePreview} alt="Reference" className="w-full h-48 object-contain" />
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-fuchsia-200 rounded-xl p-8 text-center hover:border-fuchsia-400 hover:bg-fuchsia-50 transition-all">
                      <Upload className="w-12 h-12 text-fuchsia-300 mx-auto mb-3" />
                      <p className="text-fuchsia-600 font-medium">Click to upload reference</p>
                      <p className="text-sm text-gray-400 mt-1">Your pet, person, etc.</p>
                    </div>
                    <input
                      ref={referenceInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleReferenceUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {/* Prompt Input */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
                  <span className="bg-purple-100 text-purple-700 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  Describe the Swap
                </h2>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Replace the face with my dog's face, keeping the same artistic style..."
                  className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                  rows={3}
                />
                
                {/* Suggested Prompts */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {suggestedPrompts.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPrompt(suggestion)}
                      className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-700 rounded-full transition"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>

                {/* Pro Model Toggle */}
                <div className="mt-4 flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <div>
                    <p className="font-medium text-amber-800 text-sm">Pro Model</p>
                    <p className="text-xs text-amber-600">Higher quality, slower processing</p>
                  </div>
                  <button
                    onClick={() => setUseProModel(!useProModel)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${useProModel ? 'bg-amber-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${useProModel ? 'translate-x-6' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}

              {/* Swap Button */}
              <button
                onClick={handleSwap}
                disabled={loading || !templateImage || !referenceImage}
                className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all shadow-lg ${
                  loading || !templateImage || !referenceImage
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 hover:shadow-xl hover:-translate-y-0.5'
                }`}
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Processing Magic...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Swap Photos
                  </>
                )}
              </button>
            </div>

            {/* Right Column - Result Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 h-fit lg:sticky lg:top-24">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Result
              </h2>
              
              {result ? (
                <div className="space-y-4">
                  <div className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                    <img src={result} alt="Result" className="w-full object-contain max-h-96" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center gap-2 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={handleUseInCard}
                      className="flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white rounded-xl font-medium transition"
                    >
                      Use in Card
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-violet-100 to-fuchsia-100 rounded-full flex items-center justify-center mb-4">
                    <Wand2 className="w-10 h-10 text-purple-400" />
                  </div>
                  <p className="text-gray-500 mb-2">Your swapped photo will appear here</p>
                  <p className="text-sm text-gray-400">Upload both images and describe what you want to swap</p>
                </div>
              )}
              
              {/* How it Works */}
              <div className="mt-8 p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl">
                <h3 className="font-medium text-gray-800 mb-3">How it works</h3>
                <ol className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="bg-violet-200 text-violet-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">1</span>
                    Upload a template image (the base scene/artwork)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-fuchsia-200 text-fuchsia-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">2</span>
                    Upload a reference image (your pet, person, etc.)
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-purple-200 text-purple-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">3</span>
                    Describe what you want to swap
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-pink-200 text-pink-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">4</span>
                    AI seamlessly blends them together!
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="text-purple-600 hover:text-purple-700 font-medium inline-flex items-center gap-2"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal
          initialTab="login"
          onClose={() => setShowAuth(false)}
        />
      )}
    </>
  );
};

export default PhotoSwapPage;


